import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useState, type ChangeEvent } from 'react';
import { ConfirmModal } from '@/components/ConfirmModal';
import { useTheme } from '@/contexts/ThemeContext';
import type { UseSchemaDraftResult } from '../builder/useSchemaDraft';
import { draftToJSON, jsonToDraft, type SchemaDraft } from '../builder/schema-draft';
import { WysiwygJsonReference } from './WysiwygJsonReference';

export interface WysiwygJsonTabProps {
  draft: UseSchemaDraftResult;
  /** Disable interaction during save. */
  disabled?: boolean;
  /**
   * Fires whenever the textarea's dirtiness vs. the draft changes. Lets the
   * parent screen warn before saving when the user has pasted JSON they
   * haven't yet clicked Apply on (otherwise their textarea edits would be
   * silently dropped, since Save persists the draft, not the textarea).
   */
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * Imperative handle exposed via `ref` so the parent can drive Apply from
 * outside (used when the user clicks Save with unapplied JSON edits — we
 * apply for them instead of dropping their work). Returns the parsed
 * SchemaDraft on success, null on parse failure (the tab surfaces the error
 * inline). No confirm modal — parent has already decided to apply.
 */
export interface WysiwygJsonTabHandle {
  applyNow: () => SchemaDraft | null;
}

/**
 * Advanced (raw JSON) view of the schema. Used for bulk structural edits
 * that would be tedious to do one at a time in the Design tab. Also serves
 * as a mobile fallback since the WYSIWYG editor is desktop-only.
 *
 * The textarea always reflects the current draft (it re-renders when the
 * draft changes). The user can edit freely; "Apply" parses + replaces the
 * whole draft after a confirmation modal. Parse errors surface inline.
 *
 * The displayed JSON matches the on-the-wire `CreateFormSchemaCommandData`
 * shape (no UI ids, no draft-only fields). `id` is preserved across the
 * paste round-trip so applying doesn't accidentally orphan the existing
 * record.
 */
const WysiwygJsonTabInner = forwardRef<WysiwygJsonTabHandle, WysiwygJsonTabProps>(function WysiwygJsonTabInner(
  { draft, disabled = false, onDirtyChange },
  ref,
) {
  const { theme } = useTheme();

  // Source of truth = the draft. The textarea is a controlled mirror, but
  // we let the user edit freely without round-tripping through the draft
  // until they explicitly Apply. Pulling new text on draft changes keeps
  // the view fresh after WYSIWYG edits + Apply round-trips.
  const liveJson = useMemo(() => draftToJSON(draft.draft), [draft.draft]);
  const [text, setText] = useState<string>(liveJson);
  const [parseError, setParseError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [referenceOpen, setReferenceOpen] = useState(false);

  // Track whether the textarea has unsaved local edits vs. the draft. When
  // the draft changes externally (e.g. user toggled to Design, edited, came
  // back), refresh the textarea ONLY if the user hadn't started editing.
  const lastSyncedRef = useState({ value: liveJson })[0];
  useEffect(() => {
    if (text === lastSyncedRef.value) {
      // Textarea is in sync with the previous draft snapshot — pick up the new one.
      setText(liveJson);
      setParseError(null);
      lastSyncedRef.value = liveJson;
    }
    // If the user has edits that diverge from lastSyncedRef.value, leave them
    // alone — they'll see "modified" and decide whether to Apply or Reset.
  }, [liveJson, text, lastSyncedRef]);

  const isDirty = text !== liveJson;

  // Notify the parent on every dirty-state transition so it can warn before
  // a Save would silently drop unapplied edits. Effect-based instead of fired
  // inside handleChange so external resets (e.g. tab swap, draft refresh)
  // also propagate.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useImperativeHandle(
    ref,
    () => ({
      applyNow: (): SchemaDraft | null => {
        try {
          const next = jsonToDraft(text, draft.draft.id);
          draft.setDraft(next);
          setParseError(null);
          return next;
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : 'JSON is not valid.';
          setParseError(msg);
          return null;
        }
      },
    }),
    [text, draft],
  );

  const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    setParseError(null);
    setCopied(false);
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      // Clear the indicator after a moment without forcing a re-render storm.
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for environments without async clipboard — select & copy.
      // The user can hit Ctrl+C themselves; this just silently no-ops.
    }
  }, [text]);

  const handleReset = useCallback(() => {
    setText(liveJson);
    setParseError(null);
  }, [liveJson]);

  const handleApply = useCallback(() => {
    // Validate up-front so we can show a parse error inline without going
    // through the confirm modal for invalid JSON.
    try {
      jsonToDraft(text, draft.draft.id);
      setParseError(null);
      setConfirmOpen(true);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'JSON is not valid.';
      setParseError(msg);
    }
  }, [text, draft.draft.id]);

  const handleConfirmApply = useCallback(() => {
    try {
      const next = jsonToDraft(text, draft.draft.id);
      draft.setDraft(next);
      setConfirmOpen(false);
      setParseError(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'JSON is not valid.';
      setParseError(msg);
      setConfirmOpen(false);
    }
  }, [text, draft]);

  return (
    <div
      className={
        'wysiwyg-json-tab' +
        (theme ? ' ' + theme : '') +
        (referenceOpen ? ' wysiwyg-json-tab--reference-open' : '')
      }
    >
      <ConfirmModal
        open={confirmOpen}
        title="Replace the form?"
        message="This replaces the whole form with what's in the editor below. Any sections or fields not listed there will be removed. Your changes won't be saved until you click Save Changes."
        confirmText="Replace"
        cancelText="Cancel"
        confirmClass="danger"
        theme={theme}
        onConfirm={handleConfirmApply}
        onCancel={() => setConfirmOpen(false)}
      />

      <div className="wysiwyg-json-tab-help">
        <p style={{ margin: 0 }}>
          This is the full setup of your form, written out as one block of text. Most of the
          time you'll want the Design tab. Use this view for bulk changes that would be
          tedious one field at a time. Click{' '}
          <strong>Apply</strong> to replace the form with what's in the editor below, then
          click <strong>Save Changes</strong> at the top of the page to keep it.{' '}
          <strong>Reset</strong> discards your edits and reloads the current form.
        </p>
        <button
          type="button"
          className="kstc-btn kstc-btn--secondary kstc-btn--sm wysiwyg-json-tab-reference-btn"
          onClick={() => setReferenceOpen((v) => !v)}
        >
          {referenceOpen ? 'Hide reference' : 'JSON reference'}
        </button>
      </div>

      <div className="wysiwyg-json-tab-stage">
        <div className="wysiwyg-json-tab-editor">
          <textarea
            className="wysiwyg-json-tab-textarea"
            value={text}
            onChange={handleChange}
            spellCheck={false}
            disabled={disabled}
            aria-label="Form definition JSON"
          />

          {parseError && (
            <div className="kst-error-panel" role="alert" style={{ marginTop: 8 }}>
              <strong>The text below isn't valid yet.</strong>
              <div style={{ marginTop: 4, fontSize: '0.9em', opacity: 0.85 }}>Details: {parseError}</div>
            </div>
          )}

          <div className="wysiwyg-json-tab-toolbar">
            <button
              type="button"
              className="kstc-btn kstc-btn--secondary"
              onClick={handleCopy}
              disabled={disabled}
              title="Copy the JSON to your clipboard"
            >
              {copied ? 'Copied!' : 'Copy to clipboard'}
            </button>
            <button
              type="button"
              className="kstc-btn kstc-btn--secondary"
              onClick={handleReset}
              disabled={disabled || !isDirty}
              title="Discard your edits and reload the current form definition"
            >
              Reset
            </button>
            <span className="wysiwyg-json-tab-status">
              {isDirty
                ? 'You have unsaved edits below. Click Apply to update the form, then Save Changes to keep them.'
                : 'This matches the current form.'}
            </span>
            <button
              type="button"
              className="kstc-btn kstc-btn--primary"
              onClick={handleApply}
              disabled={disabled || !isDirty}
              title="Replace the entire form with the JSON above"
            >
              Apply
            </button>
          </div>
        </div>

        {referenceOpen && (
          <WysiwygJsonReference onClose={() => setReferenceOpen(false)} />
        )}
      </div>
    </div>
  );
});

export const WysiwygJsonTab = memo(WysiwygJsonTabInner);
